/**
 * Logging severity levels
 */
export const LogLevel = {
  DEBUG: "debug",
  INFO: "info",
  WARN: "warn",
  ERROR: "error",
} as const

export type LogLevel = (typeof LogLevel)[keyof typeof LogLevel]

/**
 * CSS styles for console output
 */
const LOG_STYLES = {
  [LogLevel.DEBUG]:
    "color: #6B7280; background: #F3F4F6; padding: 2px 6px; border-radius: 3px; font-weight: 500;",
  [LogLevel.INFO]:
    "color: #2563EB; background: #DBEAFE; padding: 2px 6px; border-radius: 3px; font-weight: 500;",
  [LogLevel.WARN]:
    "color: #D97706; background: #FEF3C7; padding: 2px 6px; border-radius: 3px; font-weight: 500;",
  [LogLevel.ERROR]:
    "color: #DC2626; background: #FEE2E2; padding: 2px 6px; border-radius: 3px; font-weight: 600;",
} as const

const TIMESTAMP_STYLE = "color: #9CA3AF; font-size: 0.85em; font-weight: normal;"
const MESSAGE_STYLE = "color: inherit; font-weight: normal;"
const COMPONENT_STYLE =
  "color: #7C3AED; background: #EDE9FE; padding: 2px 6px; border-radius: 3px; font-weight: 500; margin-left: 4px;" /**
 * Metadata that can be attached to log entries
 */
export interface LogMetadata {
  [key: string]: unknown
  timestamp?: string
  component?: string
  userId?: string
  sessionId?: string
}

/**
 * Configuration for logger behavior
 */
export interface LoggerConfig {
  enableConsole?: boolean
  enableAnalytics?: boolean
  enableNotifications?: boolean
  minLogLevel?: LogLevel
  environment?: "development" | "production" | "test"
}

/**
 * Analytics event interface
 */
export interface AnalyticsEvent {
  eventName: string
  properties?: Record<string, unknown>
  timestamp: string
}

/**
 * Notification/Toast configuration
 */
export interface NotificationConfig {
  title?: string
  message: string
  type: "info" | "success" | "warning" | "error"
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

/**
 * Logger class that provides centralized logging with support for
 * console output, analytics events, and notifications/toasts
 */
export class Logger {
  private config: Required<LoggerConfig>
  private static instance: Logger | null = null

  private constructor(config: LoggerConfig = {}) {
    const isProduction = import.meta.env.MODE === "production"

    this.config = {
      enableConsole: config.enableConsole ?? true,
      enableAnalytics: config.enableAnalytics ?? false,
      enableNotifications: config.enableNotifications ?? false,
      // In production, only show INFO and above (no DEBUG)
      minLogLevel: config.minLogLevel ?? (isProduction ? LogLevel.INFO : LogLevel.DEBUG),
      environment: config.environment ?? (isProduction ? "production" : "development"),
    }
  }

  /**
   * Get or create the logger instance (Singleton pattern)
   */
  public static getInstance(config?: LoggerConfig): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(config)
    }
    return Logger.instance
  }

  /**
   * Update logger configuration
   */
  public configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * Check if a log level should be output based on configuration
   */
  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR]
    const currentLevelIndex = levels.indexOf(this.config.minLogLevel)
    const messageLevelIndex = levels.indexOf(level)
    return messageLevelIndex >= currentLevelIndex
  }

  /**
   * Internal method to write to console with custom styling
   */
  private writeToConsole(level: LogLevel, message: string, metadata?: LogMetadata): void {
    if (!this.config.enableConsole || !this.shouldLog(level)) {
      return
    }

    const timestamp = new Date().toISOString()
    const consoleMethod = {
      [LogLevel.DEBUG]: console.debug,
      [LogLevel.INFO]: console.log,
      [LogLevel.WARN]: console.warn,
      [LogLevel.ERROR]: console.error,
    }[level]

    // Build styled console output
    const component = metadata?.component ? `[${metadata.component}]` : ""

    if (component) {
      // With component tag
      consoleMethod(
        `%c${timestamp}%c %c${level.toUpperCase()}%c ${component} %c${message}`,
        TIMESTAMP_STYLE,
        "",
        LOG_STYLES[level],
        "",
        COMPONENT_STYLE,
        MESSAGE_STYLE,
      )
    } else {
      // Without component tag
      consoleMethod(
        `%c${timestamp}%c %c${level.toUpperCase()}%c %c${message}`,
        TIMESTAMP_STYLE,
        "",
        LOG_STYLES[level],
        "",
        MESSAGE_STYLE,
      )
    }

    // Log metadata if present (excluding component as it's already shown)
    if (metadata && Object.keys(metadata).length > 0) {
      const { component: _component, ...restMetadata } = metadata
      if (Object.keys(restMetadata).length > 0) {
        consoleMethod("  →", restMetadata)
      }
    }
  }

  /**
   * Send analytics event (placeholder for future implementation)
   */
  private sendAnalytics(event: AnalyticsEvent): void {
    if (!this.config.enableAnalytics) {
      return
    }

    // TODO: Implement actual analytics integration
    // Examples: Google Analytics, Mixpanel, Segment, etc.
    if (this.config.environment === "development") {
      console.debug("[ANALYTICS]", event)
    }
  }

  /**
   * Show notification/toast (placeholder for future implementation)
   */
  private showNotification(config: NotificationConfig): void {
    if (!this.config.enableNotifications) {
      return
    }

    // TODO: Implement actual notification/toast system
    // Examples: react-hot-toast, sonner, react-toastify, etc.
    if (this.config.environment === "development") {
      console.debug("[NOTIFICATION]", config)
    }
  }

  /**
   * Log a debug message
   */
  public debug(message: string, metadata?: LogMetadata): void {
    this.writeToConsole(LogLevel.DEBUG, message, metadata)
  }

  /**
   * Log an info message
   */
  public info(message: string, metadata?: LogMetadata): void {
    this.writeToConsole(LogLevel.INFO, message, metadata)
  }

  /**
   * Log a warning message
   */
  public warn(message: string, metadata?: LogMetadata): void {
    this.writeToConsole(LogLevel.WARN, message, metadata)
  }

  /**
   * Log an error message
   */
  public error(message: string, error?: Error | unknown, metadata?: LogMetadata): void {
    const enrichedMetadata: LogMetadata = { ...metadata }

    if (error instanceof Error) {
      enrichedMetadata.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      }
    } else if (error) {
      enrichedMetadata.error = error
    }

    this.writeToConsole(LogLevel.ERROR, message, enrichedMetadata)
  }

  /**
   * Track an analytics event
   */
  public track(eventName: string, properties?: Record<string, unknown>): void {
    const event: AnalyticsEvent = {
      eventName,
      properties,
      timestamp: new Date().toISOString(),
    }
    this.sendAnalytics(event)
  }

  /**
   * Show a notification/toast message
   */
  public notify(config: NotificationConfig): void {
    this.showNotification(config)

    // Also log to console based on notification type
    const level = {
      info: LogLevel.INFO,
      success: LogLevel.INFO,
      warning: LogLevel.WARN,
      error: LogLevel.ERROR,
    }[config.type]

    this.writeToConsole(level, config.message, {
      title: config.title,
      notificationType: config.type,
    })
  }

  /**
   * Convenience method for success notifications
   */
  public success(message: string, title?: string): void {
    this.notify({
      message,
      title,
      type: "success",
    })
  }
}

// Export a default instance for convenience
export const logger = Logger.getInstance({
  environment: import.meta.env.MODE === "production" ? "production" : "development",
  enableConsole: true,
  enableAnalytics: false, // Enable when analytics is implemented
  enableNotifications: false, // Enable when notification system is implemented
})
